import { useState } from "react";
import {
  RefreshCw, Key, Lock, Shield, Clock, AlertTriangle,
  CheckCircle, XCircle, Calendar,
  Database, FileKey, Eye, Layers
} from "lucide-react";
import { getRelativePercentage } from "../../../lib/utils";
import { CloudEntityQuickLinks } from "./CloudEntityQuickLinks";

interface ExpandedSecretsCredentialsCardProps {
  cloudProvider: string;
  rotation: {
    rotatedPct: number;
    pending: number;
    metrics: { rotated: number; pending: number; failed: number; scheduled: number; avgDays: number };
    ageDistribution: { range: string; count: number; percentage: number }[];
    recentRotations: { keyId: string; serviceAccount: string; rotatedAt: string; ageDays: number }[];
  };
  credentials: {
    totalKeys: number;
    agedKeys: number;
    rotationRatePct: number;
    categories: { name: string; total: number; aged: number }[];
    ageDistribution: { range: string; count: number; percentage: number }[];
    topAgedKeys: { name: string; serviceAccount: string; ageDays: number; issuedAt: string; type: string }[];
    multipleKeyServiceAccounts: { serviceAccount: string; keyCount: number }[];
  };
  secrets: {
    totalSecretsManaged: number;
    compliancePct: number;
    issuesFound: { name: string; count: number }[];
    compliantSecrets: number;
    nonCompliantSecrets: number;
    secretsByType: { type: string; count: number; compliant: number; rotationEnabled: number; aged: number }[];
    storageDistribution: { store: string; count: number; percentage: number }[];
    recentActivity: { action: string; secret: string; time: string; user: string }[];
    topExposedSecrets: { name: string; type: string; riskScore: number; risk: string; lastRotated: string; rotationEnabled: boolean }[];
  };
}

type TabId = "rotation" | "credentials" | "secrets";

export const ExpandedSecretsCredentialsCard = ({ cloudProvider, rotation, credentials, secrets }: ExpandedSecretsCredentialsCardProps) => {
  const [activeTab, setActiveTab] = useState<TabId>("rotation");

  const tabs: { id: TabId; label: string; icon: any; count?: number }[] = [
    { id: "rotation", label: "Rotation Status", icon: RefreshCw, count: rotation.metrics.pending },
    { id: "credentials", label: "Static Credentials", icon: Key, count: credentials.agedKeys },
    { id: "secrets", label: "Secrets Posture", icon: Lock, count: secrets.issuesFound.reduce((a, b) => a + b.count, 0) },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500/20 to-primary/10 ring-1 ring-sky-500/15 flex items-center justify-center">
          <Shield className="w-5 h-5 text-sky-500" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-foreground tracking-tight">Secrets & Credentials</h3>
          <p className="text-[15px] text-muted-foreground">Unified view of rotation, credentials, and secrets posture</p>
        </div>
      </div>

      {/* Summary Stats Row */}
      <div className="grid grid-cols-5 gap-3">
        {[
          { label: "Total Keys", value: credentials.totalKeys, icon: Key, color: "text-sky-500", bg: "from-sky-500/8 to-sky-500/3", border: "border-sky-500/15" },
          { label: "Aged Keys", value: credentials.agedKeys, icon: AlertTriangle, color: "text-primary", bg: "from-primary/8 to-primary/3", border: "border-primary/15" },
          { label: "Rotation Rate", value: `${rotation.rotatedPct}%`, icon: RefreshCw, color: rotation.rotatedPct >= 80 ? "text-emerald-500" : "text-sky-500", bg: rotation.rotatedPct >= 80 ? "from-emerald-500/8 to-emerald-500/3" : "from-sky-500/8 to-sky-500/3", border: rotation.rotatedPct >= 80 ? "border-emerald-500/15" : "border-sky-500/15" },
          { label: "Total Secrets", value: secrets.totalSecretsManaged, icon: Lock, color: "text-sky-500", bg: "from-sky-500/8 to-sky-500/3", border: "border-sky-500/15" },
          { label: "Compliance", value: `${secrets.compliancePct}%`, icon: Shield, color: secrets.compliancePct >= 50 ? "text-emerald-500" : "text-primary", bg: secrets.compliancePct >= 50 ? "from-emerald-500/8 to-emerald-500/3" : "from-primary/8 to-primary/3", border: secrets.compliancePct >= 50 ? "border-emerald-500/15" : "border-primary/15" },
        ].map(s => (
          <div key={s.label} className={`p-4 rounded-2xl border ${s.border} bg-gradient-to-br ${s.bg}`}>
            <s.icon className={`w-4 h-4 ${s.color} mb-2`} />
            <div className={`text-2xl font-extrabold tracking-tight tabular-nums ${s.color}`}>{s.value}</div>
            <p className="text-[11px] text-muted-foreground mt-0.5 font-medium">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 p-1 rounded-xl bg-muted/30 border border-border/30">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-semibold transition-all duration-200 ${
              activeTab === tab.id
                ? "bg-card text-foreground shadow-sm ring-1 ring-border/50"
                : "text-muted-foreground hover:text-foreground hover:bg-card/50"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                activeTab === tab.id ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
              }`}>{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "rotation" && <RotationTabContent cloudProvider={cloudProvider} data={rotation} />}
      {activeTab === "credentials" && <CredentialsTabContent cloudProvider={cloudProvider} data={credentials} />}
      {activeTab === "secrets" && <SecretsTabContent cloudProvider={cloudProvider} data={secrets} />}
    </div>
  );
};

/* ─── ROTATION TAB ──────────────────────────────── */
const RotationTabContent = ({
  cloudProvider,
  data,
}: {
  cloudProvider: string;
  data: ExpandedSecretsCredentialsCardProps["rotation"];
}) => {
  const { metrics, ageDistribution, recentRotations } = data;
  const circ = 2 * Math.PI * 14;

  return (
    <div className="space-y-5">
      {/* Metrics Grid */}
      <div className="grid grid-cols-5 gap-3">
        <div className="p-4 rounded-xl bg-success/10 border border-success/20 text-center">
          <div className="relative w-10 h-10 mx-auto mb-2">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
              <circle cx="18" cy="18" r="14" fill="none" stroke="hsl(var(--muted))" strokeWidth="2" />
              <circle cx="18" cy="18" r="14" fill="none" stroke="hsl(142 76% 36%)" strokeWidth="2"
                strokeDasharray={circ} strokeDashoffset={circ - (metrics.rotated / Math.max(metrics.rotated + metrics.pending + metrics.failed, 1)) * circ}
                strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <CheckCircle className="w-3.5 h-3.5 text-success" />
            </div>
          </div>
          <div className="text-xl font-bold text-success">{metrics.rotated}</div>
          <p className="text-[11px] text-muted-foreground">Rotated</p>
        </div>
        <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20 text-center">
          <Clock className="w-5 h-5 text-sky-500 mx-auto mb-2" />
          <div className="text-xl font-bold text-sky-500">{metrics.pending}</div>
          <p className="text-[11px] text-muted-foreground">Pending</p>
        </div>
        <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 text-center">
          <XCircle className="w-5 h-5 text-primary mx-auto mb-2" />
          <div className="text-xl font-bold text-primary">{metrics.failed}</div>
          <p className="text-[11px] text-muted-foreground">Failed</p>
        </div>
        <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20 text-center">
          <Calendar className="w-5 h-5 text-sky-500 mx-auto mb-2" />
          <div className="text-xl font-bold text-sky-500">{metrics.scheduled}</div>
          <p className="text-[11px] text-muted-foreground">Scheduled</p>
        </div>
        <div className="p-4 rounded-xl bg-muted/30 border border-border/30 text-center">
          <RefreshCw className="w-5 h-5 text-muted-foreground mx-auto mb-2" />
          <div className="text-xl font-bold text-foreground">{metrics.avgDays}</div>
          <p className="text-[11px] text-muted-foreground">Avg Days</p>
        </div>
      </div>

      {/* Age Distribution */}
      {ageDistribution.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-sky-500" /> Age Distribution
          </h4>
          <div className="h-3 rounded-full bg-muted/30 overflow-hidden flex mb-2">
            {ageDistribution.map((d, i) => {
              const colors = ["bg-emerald-500", "bg-blue-500", "bg-sky-500", "bg-primary"];
              return d.percentage > 0 ? (
                <div key={i} className={`h-full ${colors[i]} transition-all duration-700`} style={{ width: `${d.percentage}%` }} title={`${d.range}: ${d.count}`} />
              ) : null;
            })}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {ageDistribution.map((d, i) => {
              const colors = ["bg-emerald-500", "bg-blue-500", "bg-sky-500", "bg-primary"];
              return (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <div className={`w-2 h-2 rounded-full ${colors[i]}`} />
                  {d.range} <span className="font-semibold text-foreground">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Rotations */}
      {recentRotations.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-sky-500" /> Recent Rotations
          </h4>
          <div className="space-y-2">
            {recentRotations.map((rot, i) => (
              <div key={`${rot.keyId}-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-card hover:bg-muted/30 transition-all border border-border/20">
                <div className="p-1.5 rounded-md bg-emerald-500/10 shrink-0">
                  <Key className="w-3.5 h-3.5 text-emerald-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[13px] truncate" title={rot.keyId}>{rot.keyId.substring(0, 12)}...</div>
                  <div className="text-[11px] text-muted-foreground truncate" title={rot.serviceAccount}>{rot.serviceAccount.split('@')[0]}</div>
                  <CloudEntityQuickLinks
                    cloudProvider={cloudProvider}
                    entityKind="nhi"
                    entity={{
                      serviceAccountEmail: rot.serviceAccount,
                      name: rot.serviceAccount,
                      syncedAt: rot.rotatedAt,
                    }}
                    className="mt-2"
                  />
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[12px] font-bold text-foreground tabular-nums">{rot.ageDays}d ago</div>
                  <div className="text-[10px] text-muted-foreground">{new Date(rot.rotatedAt).toLocaleDateString()}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── CREDENTIALS TAB ──────────────────────────── */
const CredentialsTabContent = ({
  cloudProvider,
  data,
}: {
  cloudProvider: string;
  data: ExpandedSecretsCredentialsCardProps["credentials"];
}) => {
  const { categories, ageDistribution, topAgedKeys, multipleKeyServiceAccounts } = data;
  const maxCategoryTotal = Math.max(...categories.map((category) => Number(category?.total ?? 0)), 0);

  return (
    <div className="space-y-5">
      {/* Category Breakdown */}
      <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
        <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
          <FileKey className="w-4 h-4 text-sky-500" /> Credential Categories
        </h4>
        <div className="space-y-3">
          {categories.map((cat, i) => {
            const colors = ["from-blue-500 to-cyan-500", "from-violet-500 to-purple-500"];
            return (
              <div key={cat.name}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-medium text-foreground">{cat.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-bold tabular-nums">{cat.total}</span>
                    {cat.aged > 0 && <span className="text-[11px] text-primary font-semibold">({cat.aged} aged)</span>}
                  </div>
                </div>
                <div className="h-2 rounded-full bg-muted/30 overflow-hidden">
                  <div className={`h-full rounded-full bg-gradient-to-r ${colors[i % colors.length]} transition-all duration-700`}
                    style={{ width: `${getRelativePercentage(cat.total, maxCategoryTotal)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Age Distribution */}
      {ageDistribution.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-sky-500" /> Key Age Distribution
          </h4>
          <div className="h-3 rounded-full bg-muted/30 overflow-hidden flex mb-2">
            {ageDistribution.map((d, i) => {
              const colors = ["bg-emerald-500", "bg-blue-500", "bg-sky-500", "bg-primary"];
              return d.percentage > 0 ? (
                <div key={i} className={`h-full ${colors[i]}`} style={{ width: `${d.percentage}%` }} title={`${d.range}: ${d.count}`} />
              ) : null;
            })}
          </div>
          <div className="grid grid-cols-4 gap-2">
            {ageDistribution.map((d, i) => {
              const colors = ["bg-emerald-500", "bg-blue-500", "bg-sky-500", "bg-primary"];
              return (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <div className={`w-2 h-2 rounded-full ${colors[i]}`} />
                  {d.range} <span className="font-semibold text-foreground">{d.count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Multiple Key Accounts Warning */}
      {multipleKeyServiceAccounts.length > 0 && (
        <div className="p-4 rounded-xl bg-primary/5 border border-primary/15">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2 text-primary">
            <AlertTriangle className="w-4 h-4" /> {multipleKeyServiceAccounts.length} Accounts with Multiple Keys
          </h4>
          <div className="space-y-2">
            {multipleKeyServiceAccounts.map((acc, i) => (
              <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-card border border-primary/10">
                <div className="min-w-0 flex-1 mr-2">
                  <span className="text-[13px] font-medium text-foreground truncate block" title={acc.serviceAccount}>
                    {acc.serviceAccount.split('@')[0]}
                  </span>
                  <CloudEntityQuickLinks
                    cloudProvider={cloudProvider}
                    entityKind="nhi"
                    entity={{
                      serviceAccountEmail: acc.serviceAccount,
                      name: acc.serviceAccount,
                    }}
                    className="mt-2"
                  />
                </div>
                <span className="text-[12px] font-bold text-primary shrink-0">{acc.keyCount} keys</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Aged Keys */}
      {topAgedKeys.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-500" /> Top Aged Keys
          </h4>
          <div className="space-y-2">
            {topAgedKeys.map((key, i) => {
              const isCritical = key.ageDays > 90;
              return (
                <div key={`${key.name}-${i}`} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-colors ${isCritical ? "bg-primary/5 border-primary/20" : "bg-card border-border/20"}`}>
                  <div className={`p-1.5 rounded-md shrink-0 ${isCritical ? "bg-primary/10" : "bg-muted/20"}`}>
                    <Key className={`w-3.5 h-3.5 ${isCritical ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13px] truncate" title={key.name}>{key.name.substring(0, 16)}...</div>
                    <div className="text-[11px] text-muted-foreground truncate" title={key.serviceAccount}>{key.serviceAccount.split('@')[0]}</div>
                    <CloudEntityQuickLinks
                      cloudProvider={cloudProvider}
                      entityKind="nhi"
                      entity={{
                        serviceAccountEmail: key.serviceAccount,
                        name: key.serviceAccount,
                        secretName: key.name,
                        syncedAt: key.issuedAt,
                      }}
                      className="mt-2"
                    />
                  </div>
                  <div className="shrink-0 text-right">
                    <div className={`text-[12px] font-bold tabular-nums ${isCritical ? "text-primary" : "text-foreground"}`}>{key.ageDays}d</div>
                    <div className="text-[10px] text-muted-foreground">{key.type}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── SECRETS TAB ──────────────────────────────── */
const SecretsTabContent = ({
  cloudProvider,
  data,
}: {
  cloudProvider: string;
  data: ExpandedSecretsCredentialsCardProps["secrets"];
}) => {
  const { issuesFound, secretsByType, storageDistribution, topExposedSecrets, recentActivity } = data;

  return (
    <div className="space-y-5">
      {/* Issues Found */}
      <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
        <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-sky-500" /> Issues Found
        </h4>
        <div className="grid grid-cols-4 gap-3">
          {issuesFound.map(issue => {
            const isCritical = issue.name.includes("Aged") || issue.name.includes("Outside");
            const isWarning = issue.name.includes("rotation") || issue.name.includes("High");
            const color = issue.count === 0 ? "text-muted-foreground/40" : isCritical ? "text-primary" : isWarning ? "text-sky-500" : "text-muted-foreground";
            const bg = issue.count === 0 ? "bg-muted/10 border-border/10" : isCritical ? "bg-primary/10 border-primary/20" : isWarning ? "bg-sky-500/10 border-sky-500/20" : "bg-muted/20 border-border/20";
            return (
              <div key={issue.name} className={`p-3 rounded-lg border ${bg} text-center`}>
                <div className={`text-2xl font-bold tabular-nums ${color}`}>{issue.count}</div>
                <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">{issue.name}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Secrets by Type & Storage Distribution */}
      <div className="grid grid-cols-2 gap-4">
        {/* By Type */}
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <FileKey className="w-4 h-4 text-sky-500" /> Secrets by Type
          </h4>
          <div className="space-y-2">
            {secretsByType.map((type, i) => (
              <div key={type.type} className="p-2.5 rounded-lg bg-card border border-border/20">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[13px] font-medium text-foreground">{type.type}</span>
                  <span className="text-[13px] font-bold text-foreground">{type.count}</span>
                </div>
                <div className="flex gap-3 text-[11px] text-muted-foreground">
                  <span>Compliant: <span className="font-semibold text-foreground">{type.compliant}</span></span>
                  <span>Aged: <span className={`font-semibold ${type.aged > 0 ? "text-primary" : "text-foreground"}`}>{type.aged}</span></span>
                  <span>Rotation: <span className="font-semibold text-foreground">{type.rotationEnabled}</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Storage Distribution */}
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Database className="w-4 h-4 text-sky-500" /> Storage Distribution
          </h4>
          <div className="h-3 rounded-full bg-muted/30 overflow-hidden flex mb-3">
            {storageDistribution.map((s, i) => {
              const colors = ["bg-sky-500", "bg-violet-500", "bg-emerald-500", "bg-primary"];
              return (
                <div key={s.store} className={`h-full ${colors[i % colors.length]}`}
                  style={{ width: `${s.percentage}%` }} title={s.store} />
              );
            })}
          </div>
          <div className="space-y-2">
            {storageDistribution.map((s, i) => {
              const colors = ["bg-sky-500", "bg-violet-500", "bg-emerald-500", "bg-primary"];
              return (
                <div key={s.store} className="flex items-center justify-between p-2 rounded-lg bg-card">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${colors[i % colors.length]}`} />
                    <span className="text-[13px] font-medium">{s.store}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] text-muted-foreground">{s.count}</span>
                    <span className="text-[12px] font-bold">{s.percentage}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Top Exposed Secrets */}
      {topExposedSecrets.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-sky-500" /> Top Exposed Secrets
          </h4>
          <div className="space-y-2">
            {topExposedSecrets.slice(0, 8).map((secret, i) => {
              const isCrit = secret.risk === "critical" || secret.riskScore >= 80;
              const badgeClass = isCrit ? "bg-primary/10 text-primary border-primary/20" : "bg-sky-500/10 text-sky-500 border-sky-500/20";
              return (
                <div key={`${secret.name}-${i}`} className={`flex items-center gap-3 p-2.5 rounded-lg border transition-all ${isCrit ? "bg-primary/5 border-primary/15" : "bg-card border-border/20"}`}>
                  <div className={`p-1.5 rounded-md shrink-0 ${isCrit ? "bg-primary/10" : "bg-sky-500/10"}`}>
                    <Lock className={`w-3.5 h-3.5 ${isCrit ? "text-primary" : "text-sky-500"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[13px] truncate" title={secret.name}>{secret.name}</div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span>{secret.type}</span>
                      <span>•</span>
                      <span className="flex items-center gap-1"><Clock className="w-2.5 h-2.5" /> {secret.lastRotated}</span>
                    </div>
                    <CloudEntityQuickLinks
                      cloudProvider={cloudProvider}
                      entityKind="secret"
                      entity={secret}
                      className="mt-2"
                    />
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded border text-[10px] font-bold uppercase ${badgeClass}`}>
                    {secret.risk}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <div className="p-4 rounded-xl bg-muted/20 border border-border/30">
          <h4 className="text-[13px] font-semibold mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4 text-sky-500" /> Recent Activity
          </h4>
          <div className="space-y-2">
            {recentActivity.slice(0, 6).map((activity, i) => (
              <div key={`${activity.secret}-${i}`} className="flex items-center gap-3 p-2 rounded-lg bg-card border border-border/20 hover:bg-muted/30 transition-all">
                <div className={`p-1.5 rounded-md shrink-0 ${activity.action === "Accessed" ? "bg-sky-500/10" : "bg-emerald-500/10"}`}>
                  {activity.action === "Accessed" ? <Eye className="w-3 h-3 text-sky-500" /> : <RefreshCw className="w-3 h-3 text-emerald-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-500 font-medium">{activity.action}</span>
                    <span className="font-medium text-[13px] truncate">{activity.secret}</span>
                  </div>
                  <CloudEntityQuickLinks
                    cloudProvider={cloudProvider}
                    entityKind="secret"
                    entity={{
                      name: activity.secret,
                      secretName: activity.secret,
                      syncedAt: activity.time,
                    }}
                    className="mt-2"
                  />
                </div>
                <div className="text-[11px] text-muted-foreground shrink-0">
                  {new Date(activity.time).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
